import { useState, useEffect } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  makeStyles,
} from '@material-ui/core'

import ExpansionPanel from '@material-ui/core/ExpansionPanel'
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary'
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails'

import { map, forEach, isEmpty } from 'lodash'

import TaskActionButton from '../TaskActionButton/TaskActionButton'

import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import CustomDropzone from './CustomDropzone'
import FilesList from './FileList'

const DialogContainer = styled.div``

const useStyles = makeStyles(() => ({
  chip: {
    background: '#fff',
    border: '1px solid black',
    marginRight: '10px',
  },
  uploadButton: {
    background: '#3d70d6',
    border: '1px solid #2951a3',
    borderRadius: '8px',
    color: '#fff',
    height: '32px',
    width: '125px',
  },
  disableButton: {
    background: '#e6e7ea',
    color: '#8b8f98',
  },
  panel: {
    backgroundColor: '#F0F2F4',
    marginBottom: 10,
    borderRadius: 0,
    '&:before': {
      display: 'none',
    },
  },
  greenProgress: {
    '& .MuiLinearProgress-bar1Buffer': {
      backgroundColor: 'green',
    },
  },
}))

const UploadFiles = ({
  tcin = '',
  uploadFileHandler = () => {},
  fileStatesList = {},
  removeFiles = () => {},
  maxFileSize = 1000000 * 2048, // 2 GB limit
  submitLoading = false,
  dropzoneArray = [
    {
      text: 'Drag and drop or browse',
      fileType: 'DEFAULT',
      acceptedFileTypes: ['.zip'],
    },
  ],
  hideDialogActions = false,
  openDefaultDialog = false,
  primaryText = '',
  filesLimit = 10,
  getAllFilesInTheDroppedFolder = false,
}) => {
  const styles = useStyles()

  const [dialogOpen, setDialogOpen] = useState(openDefaultDialog)
  const [uploadButtonType, setUploadButtonType] = useState('upload')
  const [sendForReviewDisabled, setSendForReviewDisabled] = useState(true)
  const [progressObj, setProgressObj] = useState({
    successCount: 0,
    errorCount: 0,
    totalFiles: 0,
  })

  const handleOpen = (event) => {
    event.stopPropagation()
    event.preventDefault()
    setDialogOpen(true)
  }

  const handleClose = (event) => {
    event.stopPropagation()
    event.preventDefault()
    setDialogOpen(false)
  }

  const addFiles = (newFiles, fileType, folderName = '') => {
    if (getAllFilesInTheDroppedFolder) {
      uploadFileHandler(newFiles, fileType, folderName)
    } else {
      newFiles.forEach((file) => uploadFileHandler(file, fileType, folderName))
    }
  }

  useEffect(() => {
    let successCount = 0
    let errorCount = 0
    const fileList = map(fileStatesList, (fileStates) => {
      if (fileStates.status === 'success') {
        successCount++
      }
      if (fileStates.status === 'error') {
        errorCount++
      }
      return ''
    })
    if (errorCount > 0) {
      setUploadButtonType('error')
    } else if (successCount < fileList.length) {
      setUploadButtonType('uploading')
    } else if (successCount > 0 && successCount === fileList.length) {
      setUploadButtonType('success')
    } else {
      setUploadButtonType('upload')
    }
    setSendForReviewDisabled(
      successCount !== fileList.length || fileList.length === 0
    )

    setProgressObj({
      successCount,
      errorCount,
      totalFiles: fileList.length,
    })
  }, [fileStatesList])

  let color = 'blue'

  const { totalFiles, errorCount } = progressObj
  let progress = 0

  if (totalFiles > 0 && !isEmpty(fileStatesList)) {
    forEach(fileStatesList, (file) => {
      progress += file.progress
    })
    progress = progress / totalFiles
  }
  if (errorCount > 0) {
    color = '#CC0000'
  } else if (progress === 100) {
    color = 'green'
  }

  return (
    <div
      onClick={(event) => {
        event.stopPropagation()
        event.preventDefault()
      }}
    >
      <TaskActionButton
        buttonType={uploadButtonType}
        onClick={handleOpen}
        color={color}
        progress={progress}
      />
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={dialogOpen}
        maxWidth="md"
        fullWidth
      >
        {submitLoading ? <LinearProgress /> : <div style={{ height: 4 }} />}
        <DialogContainer>
          <DialogTitle id="customized-dialog-title" onClose={handleClose}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>Upload Files for {tcin}</div>
              <Button color="primary" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </DialogTitle>
          <DialogContent dividers>
            {dropzoneArray.map((o, i) => (
              <ExpansionPanel key={i} elevation={0} className={styles.panel}>
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{o.text}</Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails style={{ flexDirection: 'column' }}>
                  <CustomDropzone
                    text={o.text}
                    maxFileSize={maxFileSize}
                    acceptedFiles={o.acceptedFileTypes}
                    filesLimit={filesLimit}
                    onAdd={(newFiles, folderName) =>
                      addFiles(newFiles, o.fileType, folderName)
                    }
                  />
                  <FilesList
                    uploadFileHandler={uploadFileHandler}
                    removeFiles={removeFiles}
                    files={fileStatesList}
                    fileCategory={o.fileType}
                  />
                </ExpansionPanelDetails>
              </ExpansionPanel>
            ))}
          </DialogContent>
          {hideDialogActions ? null : (
            <DialogActions>
              <Button
                autoFocus
                onClick={handleClose}
                variant="contained"
                color="primary"
                disabled={sendForReviewDisabled || submitLoading}
              >
                {primaryText || 'Done'}
              </Button>
            </DialogActions>
          )}
        </DialogContainer>
      </Dialog>
    </div>
  )
}

UploadFiles.propTypes = {
  fileStatesList: PropTypes.object,
  maxFileSize: PropTypes.number,
  removeFiles: PropTypes.func,
  tcin: PropTypes.string,
  uploadFileHandler: PropTypes.func,
}

export default UploadFiles
